import React, { useContext, useEffect, useMemo } from 'react';
import { Form, Row, Col, Input, FormInstance, Divider, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import TagsManagement from '@components/TagsManagement';
import useSwr from '@hooks/useSwr';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import SelectMotLoraContracts from '@components/SelectMotLoraContracts';
import SessionContext from '@providers/Session/SessionContext';
import UploadStep from './UploadStep';

const { Item } = Form;

export interface DetailsStepDataProps {
  devEui: string;
  appEui: string;
  contractId: string;
  description?: string;
  tags?: string[];
  appKey?: string;
  devAddr?: string;
  nwksKey?: string;
  appsKey?: string;
  customerId?: string;
  contractMotId?: string;
  contractLorawanId?: string;
  requestId?: number;
}

type ContractProps = {
  vendor: string;
  planName: string;
  assetNumber: string;
  acronym: string;
  flagIsca: string;
  nickname: string;
  id: string;
};

interface DetailsStepProps {
  form: FormInstance<DetailsStepDataProps>;
  isBatch: boolean;
  isToken?: boolean;
  onUpload: (values: { [key: string]: string }[] | undefined) => void;
  renderItemLabel: (label: string) => JSX.Element;
  renderInputItem: (
    label: string,
    numSize?: number,
    validateNumSize?: boolean,
  ) => JSX.Element;
  activation?: 'OTAA' | 'ABP';
  encryption?: 'NS' | 'APP';
}

const DetailsStep = ({
  form,
  isBatch,
  isToken,
  onUpload,
  renderItemLabel,
  renderInputItem,
  activation,
  encryption,
}: DetailsStepProps) => {
  const { t } = useTranslation('lorawan');
  const formValues = Form.useWatch([], form);
  const { keycloak } = useContext(KeycloakContext);
  const { isMot } = useContext(SessionContext);
  const customerId = keycloak?.idTokenParsed?.customerId;

  const isActivationOtaa = activation === 'OTAA';
  const isActivationAbp = activation === 'ABP';
  const isEncryptionNs = encryption === 'NS';
  const isEncryptionApp = encryption === 'APP';

  const { data: dataContracts } = useSwr<ContractProps[]>(
    customerId ? '/service-proxy/contracts' : null,
    {
      params: {
        customerId,
        product: 'LORAWAN',
      },
    },
  );

  const contractOptions = useMemo(
    () =>
      Array.isArray(dataContracts)
        ? dataContracts?.map(({ id, planName, assetNumber, nickname }) => ({
            label: `${assetNumber}${
              nickname ? ` - ${nickname}` : ''
            } - ${planName}`,
            value: id,
          }))
        : [],
    [dataContracts],
  );

  useEffect(() => {
    if (contractOptions?.length === 1) {
      form.setFieldValue('contractId', contractOptions[0].value);
    }
  }, [contractOptions, form]);

  return (
    <Form
      colon={false}
      form={form}
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      scrollToFirstError
      initialValues={{ activation: 'OTAA', encryption: 'NS' }}
      layout="vertical"
    >
      {isMot ? (
        <SelectMotLoraContracts form={form} span={24} />
      ) : (
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Item
              label={t('contract')}
              name="contractId"
              rules={[
                {
                  required: true,
                  message: t('fieldIsRequired'),
                },
              ]}
            >
              <Select
                optionFilterProp="label"
                options={contractOptions}
                loading={!contractOptions}
                disabled={!contractOptions || contractOptions?.length === 1}
                showSearch
                style={{ width: '100%' }}
              />
            </Item>
          </Col>
        </Row>
      )}

      <Divider style={{ marginTop: 16 }} />
      {isBatch ? (
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col span={24}>
            <Item
              name="uploadedDevices"
              rules={[
                {
                  required: true,
                  message: t('fieldIsRequired'),
                },
              ]}
            >
              <UploadStep
                isToken={isToken}
                onUpload={values => {
                  onUpload(values);
                  form.setFieldValue('uploadedDevices', values);
                }}
              />
            </Item>
          </Col>
        </Row>
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} sm={12}>
              {renderInputItem('devEui', 16, true)}
            </Col>
            <Col xs={24} sm={12}>
              {renderInputItem('appEui', 16, true)}
            </Col>
          </Row>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Item label={t('description')} name="description">
                <Input allowClear />
              </Item>
            </Col>
            <Col span={24}>
              <Item label={renderItemLabel('tags')} name="tags">
                <TagsManagement
                  tags={formValues?.tags ?? []}
                  onChange={newTags => {
                    form.setFieldValue('tags', newTags);
                  }}
                />
              </Item>
            </Col>
          </Row>
          {/*  
      -----------------------------------------------------
      ATTRIBUTES FROM THE TYPE OF ACTIVATION AND ENCRYPTION
      -----------------------------------------------------
      OTAA + NS = Only 'appKey'
      OTAA + APP = None
      ABP + NS = 'devAddr', 'nwksKey' and 'appsKey'
      ABP + APP = 'devAddr' and 'nwksKey'
      -----------------------------------------------------
      -----------------------------------------------------
      */}
          {!(isActivationOtaa && isEncryptionApp) && (
            <Divider orientation="left">{t('security')}</Divider>
          )}
          <Row gutter={[8, 8]} style={{ marginTop: 24 }}>
            {isActivationOtaa && isEncryptionNs && (
              <Col span={24}>{renderInputItem('appKey', 32, true)}</Col>
            )}
            {isActivationAbp && (
              <>
                <Col span={24}>{renderInputItem('devAddr', 8, true)}</Col>
                <Col span={24}>{renderInputItem('nwksKey', 32, true)}</Col>
                {isEncryptionNs && (
                  <Col span={24}>{renderInputItem('appsKey', 32, true)}</Col>
                )}
              </>
            )}
          </Row>
        </>
      )}
    </Form>
  );
};

export default DetailsStep;
